import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import { api } from '../../services/api.js';
import { mapServerResearchToLocal, isPending } from '../../services/apiUtils.js';
import { FREE_RESEARCH_LIMITS } from '../../payment/PricingUtils.js';
import { event } from '../../services/analyticsService.js';
import Question from '../../components/Question.js';
import ReadOnlyQuestion from '../../components/ReadOnlyQuestion.js'; // <-- NEW IMPORT
import { validateSurvey, enoughCredits, deepCloneSurvey, validateQuestionFieldChange, surveyDiffers } from './SurveyUtils.js';
import WarningSnackbar from '../../components/WarningSnackbar.js';

function SurveyEditor({ 
  userData, 
  setUserData, 
  onTopicUpdate, 
  setTabIndex, 
  progress,
  setProgress,
  isAdmin, 
  hasActiveSubscription, 
  walletBalance, 
  // onDuplicateResearch,
  credits, 
  updateUserAttributes,
  unsavedSurveyChanges,
  setUnsavedSurveyChanges,
  onDuplicateResearch,
  readOnly // <-- READONLY PROP
}) {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  
  // When initializing:
  const [editedSurvey, setEditedSurvey] = useState(deepCloneSurvey(userData.surveyQuestions || []));

  const hasResponses = userData.surveyResponses && userData.surveyResponses.length > 0;


  useEffect(() => {
    setEditedSurvey(deepCloneSurvey(userData.surveyQuestions || []));
  }, [userData.surveyQuestions]);


  useEffect(() => {
    if (unsavedSurveyChanges) {
      setWarningMessage("Please save your changes before moving to the next tab.")
      setOpenWarningSnackbar(true);
    } else {
      setOpenWarningSnackbar(false);
    }
  }, [unsavedSurveyChanges]);


  const renderAddQuestionButton = () => {
    if (readOnly) return null;

    const maxQuestions = isAdmin 
      ? 20 // Admin limit
      : hasActiveSubscription 
        ? 15 // SUBSCRIPTION_LIMITS.MAX_QUESTIONS
        : 10; // FREE_RESEARCH_LIMITS.MAX_QUESTIONS

    if (editedSurvey.length >= maxQuestions) {
      return null; // Don't show the button if at or over the limit
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Button onClick={addQuestion}>
          Add Question
        </Button>
      </Box>
    );
  };


  const handleContinue = async () => {
    if (readOnly) return;
    try {
      const hasCredits = await enoughCredits(progress, isAdmin, hasActiveSubscription, walletBalance);
      // If no credits and no subscription, show error
      if (!hasCredits) {
        setWarningMessage(
          "Please sign up to our beta to get unrestricted access to our platform"
        );
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;

      }
    } catch (error) {
      console.error("Error checking credits:", error);
    }

    if (surveyDiffers(editedSurvey, userData.surveyQuestions)) {
      if (hasResponses) {
        // If there are responses, warn about credit cost
        if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
          setLoading(false);
          return;
        }
        const proceed = window.confirm(
          'You made changes to your survey. You will have to re-generate responses after saving these changes. Do you want to proceed?'
        );
        if (proceed) {
          handleSaveUpdate();
        } else {
          return;
        }
      } else {
        // If no responses, just save without warning
        handleSaveUpdate();
      }
    } else {

      const validationResult = validateSurvey(userData.surveyQuestions, { isAdmin, hasActiveSubscription, FREE_RESEARCH_LIMITS });
      if (!validationResult.valid) {
        setWarningMessage(validationResult.message);
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;

      } else {
        nextTab();
      }
      
    }


  };

  const nextTab = () => {
    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(false);
    }

    setTabIndex(1);
    if (progress < 2) {
      setProgress(2);
    }
    

    setLoading(false);
  }


  const handleSaveUpdate = async () => {
    if (readOnly) return;
    try {
      setLoading(true);

      // Use new validation logic
      const validationResult = validateSurvey(editedSurvey, { isAdmin, hasActiveSubscription, FREE_RESEARCH_LIMITS });
      if (!validationResult.valid) {
        setWarningMessage(validationResult.message);
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;
      }
      

      console.log("📤 Sending questions to backend:", JSON.stringify(editedSurvey, null, 2));

      // Update the survey
      const result = await api.updateSurvey(
        userData.userID,
        userData.researchID,
        editedSurvey,
      );
      
      const localResult = mapServerResearchToLocal(result);

      setEditedSurvey(localResult.surveyQuestions);


      // Track successful survey completion
      event({
        action: 'research_step_completed',
        category: 'tool_usage',
        label: 'step_1_survey_creation',
        value: editedSurvey.length, // Number of questions as value
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

      const newUserData = {...userData, surveyQuestions: localResult.surveyQuestions, surveyStatus: 'COMPLETED', lastModified: Math.floor(Date.now() / 1000), responsesStatus: result.responsesStatus}

      console.log("newUserData in handleSaveUpdate is", newUserData);

      setUserData(newUserData);

      onTopicUpdate(newUserData);

      nextTab();

    } catch (err) {
      // Track error
      event({
        action: 'research_step_error',
        category: 'error',
        label: `survey_completion_error: ${err.message}`,
      });
      console.log("Error in updating survey", err);
      setWarningMessage('Failed to update survey: ' + err.message);
      setOpenWarningSnackbar(true);
      setLoading(false);
    }
  };

  const addQuestion = () => {
    if (readOnly) return;
    setEditedSurvey([...editedSurvey,   { 
      questionText: '', 
      questionType: 'open-ended', 
      responseOptions: [], 
      maxLength: 200,
      hasFixedPercentages: false,
      responsePercentages: [],
      selectMax: '',
      selectMin: ''
    }]);

    // Track question addition
    event({
      action: 'add_question',
      category: 'tool_usage',
      label: 'survey_editor',
      value: editedSurvey.length + 1, // New question count
    });

    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    if (readOnly) return;
    const updatedQuestions = [...editedSurvey];
    updatedQuestions[index][field] = value;

    const question = updatedQuestions[index];


    const validationResult = validateQuestionFieldChange(question, field, value);
    if (!validationResult.valid) {
      setWarningMessage(validationResult.message);
      setOpenWarningSnackbar(true);
  
    } 
    updatedQuestions[index][field] = validationResult.adjustedValue;
    if (validationResult.valid) {
      setEditedSurvey(updatedQuestions);
      if (setUnsavedSurveyChanges) {
        setUnsavedSurveyChanges(true);
      }
    }


  };

  const removeQuestion = async (index) => {
    if (readOnly) return;
    if (editedSurvey.length <= 1) {
      setWarningMessage('Cannot remove the last question. The survey must have at least one question.');
      setOpenWarningSnackbar(true);
      return; 
    }

    console.log("Length of editedSurvey before removal:", editedSurvey.length);

    const updatedSurvey = [...editedSurvey];
    updatedSurvey.splice(index, 1);
    setEditedSurvey(updatedSurvey);

    console.log("Length of editedSurvey after removal:", updatedSurvey.length);

    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };

  const moveQuestionUp = async (index) => {
    if (readOnly) return;
    if (index === 0) return;
    
    const updatedSurvey = [...editedSurvey];
    const temp = updatedSurvey[index];
    updatedSurvey[index] = updatedSurvey[index - 1];
    updatedSurvey[index - 1] = temp;
    
    setEditedSurvey(updatedSurvey);
    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };  

  const moveQuestionDown = async (index) => {
    if (readOnly) return;
    if (index === editedSurvey.length - 1) return;
    
    const updatedSurvey = [...editedSurvey];
    const temp = updatedSurvey[index];
    updatedSurvey[index] = updatedSurvey[index + 1];
    updatedSurvey[index + 1] = temp;
    
    setEditedSurvey(updatedSurvey);
    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    if (readOnly) return;
    const updatedSurvey = [...editedSurvey];
    const question = updatedSurvey[questionIndex];

    question.responseOptions.splice(optionIndex, 1);

    const numOptions = question.responseOptions.length;

    if (question.selectMin > numOptions) {
      setWarningMessage(`Min cannot exceed the number of options (${numOptions}). Adjusting min to ${numOptions}.`);
      question.selectMin = numOptions;
      setOpenWarningSnackbar(true);
    }

    if (question.questionType === 'multiple-choice' && question.responseOptions.length < 2) {

      setWarningMessage(
        `Question ${questionIndex + 1} now has only ${question.responseOptions.length} option(s). You must have at least 2 before continuing.`)

      setOpenWarningSnackbar(true);
    }
    setEditedSurvey(updatedSurvey);
    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };

  const addOption = (questionIndex) => {
    if (readOnly) return;
    const updatedSurvey = [...editedSurvey];
    updatedSurvey[questionIndex].responseOptions.push('');
    setEditedSurvey(updatedSurvey);

    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };

  const toggleQuestionType = (questionIndex, newType) => {
    if (readOnly) return;
    const updatedSurvey = [...editedSurvey];
    updatedSurvey[questionIndex].questionType = newType;
    if (newType === 'multiple-choice') {
      updatedSurvey[questionIndex].selectMin = 1;
      updatedSurvey[questionIndex].selectMax = 1;
    }
    setEditedSurvey(updatedSurvey);
    if (setUnsavedSurveyChanges) {
      setUnsavedSurveyChanges(true);
    }
  };


  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box>
        {showSuccess && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
            Successfully saved survey
          </Alert>
        )}

        {editedSurvey && editedSurvey.length > 0 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {readOnly ? 'Survey Questions' : 'Edit Survey'} {/* Title change for read-only mode */}
            </Typography>

            {/* CONDITIONAL RENDERING OF QUESTION COMPONENT */}
            {editedSurvey.map((question, index) => (
              readOnly ? (
                <ReadOnlyQuestion
                  key={index}
                  question={question}
                  index={index}
                />
              ) : (
              <Question
                key={index}
                question={question}
                index={index}
                handleQuestionChange={handleQuestionChange}
                removeQuestion={removeQuestion}
                addOption={addOption}
                removeOption={removeOption}
                toggleQuestionType={toggleQuestionType}
                moveQuestionUp={moveQuestionUp}
                moveQuestionDown={moveQuestionDown}
                totalQuestions={editedSurvey.length}
                  // No 'readOnly' prop passed to Question.js, as requested.
              />
              )
            ))}

            {renderAddQuestionButton()}

            {!readOnly && (
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={loading || openWarningSnackbar}
              sx={{ mt: 2, display: 'block', mx: 'auto'}}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Saving...
                </Box>
              ) : (
                'Save and Continue →'
              )}
            </Button>
            )}

          </Paper>
        )}

        <WarningSnackbar
          open={openWarningSnackbar}
          message={warningMessage}
          onClose={() => setOpenWarningSnackbar(false)}
        />

      </Box>
    </ThemeProvider>
  );
}

export default SurveyEditor;